/** Shared helpers for post-payment double-insurance re-issue UX. */

export const formatPolicyDate = (dateString) => {
  if (!dateString) return '—';
  const raw = String(dateString).trim();
  const iso = /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : raw;
  const parsed = iso.includes('/')
    ? (() => {
        const [day, month, year] = iso.split('/');
        return new Date(Number(year), Number(month) - 1, Number(day));
      })()
    : new Date(iso.includes('T') ? iso : `${iso}T12:00:00`);
  if (isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const policyDisplayDateToYmd = (value) => {
  const s = String(value || '').trim();
  if (!s) return null;
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slash) {
    return `${slash[3]}-${String(slash[2]).padStart(2, '0')}-${String(slash[1]).padStart(2, '0')}`;
  }
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  return null;
};

export const addCalendarDayYmd = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || '').trim());
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 1));
  return d.toISOString().slice(0, 10);
};

/** Day after latest end (DMVIC DD/MM/YYYY or ISO). Used for double-insurance re-issue UX. */
export const nextAvailableStartAfterPolicyEnd = (latestEndDisplayDate) => {
  const endYmd = policyDisplayDateToYmd(latestEndDisplayDate);
  return endYmd ? addCalendarDayYmd(endYmd) : null;
};

export const latestPolicyEndFromList = (policies) => {
  if (!Array.isArray(policies) || policies.length === 0) return null;
  let latestYmd = null;
  let latestRaw = null;
  for (const policy of policies) {
    const raw = policy?.endDate || policy?.CoverEndDate;
    const ymd = policyDisplayDateToYmd(raw);
    if (ymd && (!latestYmd || ymd > latestYmd)) {
      latestYmd = ymd;
      latestRaw = raw;
    }
  }
  return latestRaw;
};

/**
 * Transient ValidateDoubleInsurance probe outages (HTTP 5xx / timeout / network).
 * These contain the words "double insurance" but are NOT real active-cover hits —
 * do not show the re-issue panel for them.
 */
export const isTransientDoubleInsuranceProbeFailure = (err) => {
  const s = String(err || '');
  return (
    /double insurance validation failed/i.test(s) &&
    /(status code 5\d\d|timeout|ECONN|ENOTFOUND|network|socket)/i.test(s)
  );
};

export const isDoubleInsuranceFailure = (errorCode, errorText, errorMessage) => {
  const combined = `${errorCode || ''} ${errorText || ''} ${errorMessage || ''}`;
  if (isTransientDoubleInsuranceProbeFailure(combined)) return false;
  const lower = combined.toLowerCase();
  return errorCode === 'ER005' || errorCode === 'DOUBLE_INSURANCE' || lower.includes('double insurance');
};
